import React, { useState } from 'react';
import { ExternalLink, Check, ChevronDown, Info } from 'lucide-react';
import { motion } from 'framer-motion';

const ProductCard = ({
  step,
  name,
  product,
  example,
  why,
  price,
  quantity,
  duration,
  badges = [],
  searchUrl,
  tiraUrl,
  keyIngredients = [],
  usageNote,
  variant = 'morning',
  hasOptions = false,
  selectedOption,
  onOptionChange
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const variants = {
    morning: {
      gradient: 'from-amber-500 to-orange-500',
      bg: 'from-amber-50 to-orange-50'
    },
    evening: {
      gradient: 'from-indigo-500 to-purple-500',
      bg: 'from-indigo-50 to-purple-50'
    }
  };

  const colors = variants[variant];

  return (
    <motion.div
      className="bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-violet-100 my-3"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
    >
      {/* Top colored stripe */}
      <div className={`h-2 bg-gradient-to-r ${colors.gradient}`} />

      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
          <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br ${colors.gradient} rounded-xl flex items-center justify-center shadow-lg text-white font-bold text-base sm:text-lg`}>
            {step}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[11px] sm:text-xs text-gray-500 uppercase tracking-wide font-semibold">{name}</span>
              {hasOptions && (
                <div className="flex gap-1.5">
                  <button
                    onClick={() => onOptionChange('budget')}
                    className={`px-2 py-0.5 rounded-md text-xs font-semibold transition-all ${
                      selectedOption === 'budget'
                        ? `bg-gradient-to-r ${colors.gradient} text-white shadow-md`
                        : 'bg-gray-100 text-gray-600 hover:bg-violet-50 border border-gray-200'
                    }`}
                  >
                    Budget
                  </button>
                  <button
                    onClick={() => onOptionChange('premium')}
                    className={`px-2 py-0.5 rounded-md text-xs font-semibold transition-all ${
                      selectedOption === 'premium'
                        ? `bg-gradient-to-r ${colors.gradient} text-white shadow-md`
                        : 'bg-gray-100 text-gray-600 hover:bg-pink-50 border border-gray-200'
                    }`}
                  >
                    Premium
                  </button>
                </div>
              )}
            </div>
            <h3 className="font-bold text-sm sm:text-base lg:text-lg mb-2 text-gray-900 leading-tight">{product}</h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-3">{why}</p>
          </div>
        </div>

        {/* Badges */}
        {badges?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
            {badges.map((badge, i) => (
              <span key={i} className="inline-flex items-center text-[10px] sm:text-xs px-2 sm:px-3 py-1 sm:py-1.5 bg-violet-50 text-violet-700 rounded-md font-medium border border-violet-200">
                <Check className="w-3 h-3 mr-0.5 sm:mr-1" />
                {badge}
              </span>
            ))}
          </div>
        )}

        {/* Price Info */}
        {price && (
          <div className="flex items-center gap-3 sm:gap-4 lg:gap-6 mb-3 sm:mb-4 pb-3 sm:pb-4 border-b border-gray-200">
            <div>
              <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Price</p>
              <h4 className={`text-base sm:text-lg font-bold bg-gradient-to-r ${colors.gradient} bg-clip-text text-transparent`}>
                ₹{price}
              </h4>
            </div>
            <div>
              <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Size</p>
              <p className="text-xs sm:text-sm font-medium text-gray-900">{quantity}</p>
            </div>
            <div>
              <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Duration</p>
              <p className="text-xs sm:text-sm font-medium text-gray-900">{duration}</p>
            </div>
          </div>
        )}

        {/* Expandable How to Use Section */}
        {usageNote && (
          <motion.div
            initial={false}
            animate={{ height: isExpanded ? 'auto' : 0 }}
            className="overflow-hidden"
          >
            <div className={`mb-3 sm:mb-4 p-3 sm:p-4 rounded-xl bg-gradient-to-br ${colors.bg} border border-violet-100`}>
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 sm:w-5 sm:h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-0.5 sm:mb-1 text-xs sm:text-sm">How to Use</h4>
                  <p className="text-xs sm:text-sm text-gray-700">{usageNote}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Actions */}
        <div className="flex gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
          {usageNote && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex-shrink-0 px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold border-2 border-violet-300 text-violet-700 rounded-lg hover:bg-violet-50 transition-all inline-flex items-center"
            >
              {isExpanded ? 'Show Less' : 'How to Use'}
              <ChevronDown className={`w-3 h-3 sm:w-4 sm:h-4 ml-1.5 sm:ml-2 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
          )}
          {searchUrl && (
            <a
              href={searchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex-1 px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold bg-gradient-to-r ${colors.gradient} text-white rounded-lg hover:opacity-90 transition-all inline-flex items-center justify-center shadow-md`}
            >
              <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
              Search on Nykaa
            </a>
          )}
          {tiraUrl && (
            <a
              href={tiraUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex-1 px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold bg-gradient-to-r ${colors.gradient} text-white rounded-lg hover:opacity-90 transition-all inline-flex items-center justify-center shadow-md`}
            >
              <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
              Search on Tira
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
